/**
 * Forward-only cursor paging, as a URL param.
 *
 * Two lists in this app page this way — /sessions and the vault approvals
 * queue — and both do it for the same reason: the API's `before` is a row
 * id, so there is no way to derive the cursor for page N without having
 * walked there. The walk itself is therefore what gets stored. The trail is
 * both the "where am I" (its length) and the "how do I go back" (pop one),
 * which is why a separate page number is never kept beside it: that would
 * be a second source of truth able to disagree with the cursor it sits next
 * to.
 *
 * It lives in the URL rather than in component state because both routes
 * poll. Every loader run replaces the loader data the page renders from, so
 * paging held in a `useState` immediately disagrees with the rows beside it
 * — see `VaultAudit.tsx`'s comment for the worked example of what that
 * costs. In the URL it survives the tick, survives reload, and makes a
 * position something an operator can paste into a ticket.
 *
 * Extracted from `sessions-search.ts` when the approvals queue became the
 * second caller; `sessions-search.ts` re-exports these so no call site or
 * test had to move.
 */

/** Separator for the trail. Not a character that appears in a uuid, and
 * URL-safe enough that the address bar stays readable. */
const TRAIL_SEPARATOR = ','

/**
 * Both APIs validate a cursor as `z.uuid()`, so anything else in the trail
 * is a 400 — and a 400 inside a loader is an error boundary, not the "land
 * on the default view" these parsers promise for mangled input.
 * `?cursors=cat` did exactly that.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Splits the trail param, dropping empties so a stray trailing comma
 * cannot inflate the page number, and dropping anything that is not a uuid
 * so a hand-edited URL degrades to page 1 rather than to the error
 * boundary. */
export function cursorTrail(cursors: string | undefined): string[] {
  if (cursors === undefined) return []
  return cursors.split(TRAIL_SEPARATOR).filter((cursor) => UUID_RE.test(cursor))
}

/** Serializes a trail back into a search param. An empty trail is
 * `undefined` so page 1 has no `cursors=` in its URL at all. */
export function trailParam(trail: readonly string[]): string | undefined {
  return trail.length === 0 ? undefined : trail.join(TRAIL_SEPARATOR)
}

/** Where a trail puts you: 1-based, matching what `Pagination` renders, and
 * the `before` cursor for this page (`undefined` on page 1). */
export interface CursorPosition {
  page: number
  before: string | undefined
  trail: readonly string[]
}

export function cursorPosition(cursors: string | undefined): CursorPosition {
  const trail = cursorTrail(cursors)
  return {
    page: trail.length + 1,
    // `noUncheckedIndexedAccess` makes this `string | undefined` for free,
    // which is exactly right: an empty trail is page 1 and has no cursor.
    before: trail[trail.length - 1],
    trail,
  }
}

/**
 * The next trail after a page move, or `undefined` when the move is not
 * available.
 *
 * Forward-only: the trail IS the history, so the only two moves are push
 * this page's cursor and pop the last one. `Pagination` never asks for a
 * jump, and a null `nextCursor` means Next is already disabled, so the
 * forward guard is belt and braces rather than the only thing stopping it.
 */
export function movedTrail(
  position: CursorPosition,
  wantedPage: number,
  nextCursor: string | null,
): readonly string[] | undefined {
  if (wantedPage > position.page) {
    if (nextCursor === null) return undefined
    return [...position.trail, nextCursor]
  }
  return position.trail.slice(0, -1)
}
